import axios from "axios";
import API_BASE_URL from "../../../config/apiConfig";
import { logEndpointError } from "../debug/endpointLogger";

const API_URL = `${API_BASE_URL}/api/LabPhlebotomy`;

// ============================================================
// Servicio de Lab Phlebotomy
// Controller: LabPhlebotomyController
// Ruta base: /api/LabPhlebotomy
// Autenticación: JWT Bearer (todos los endpoints)
// Total funciones: 6
// ============================================================

const authHeader = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
});

/**
 * Crea un registro de Lab Phlebotomy. Requiere un Specialist Referral activo
 * (relación 1:1). No permite fechas futuras.
 *
 * @param {Object} data
 * @param {number} data.specialistReferralId - ID del Specialist Referral asociado (1:1)
 * @param {string} data.cipId - CIP ID del paciente
 * @param {string} data.dateOfService - Fecha de servicio (YYYY-MM-DD, no futura)
 * @param {number} data.appointmentTypeId - Tipo de cita (FK TAB_STATUSES)
 * @param {number} data.pcpId - PCP asignado (FK TAB_EMPLOYEES)
 * @param {number} data.locationId - Centro médico (FK TAB_LOCATIONS)
 * @param {number} data.employeeStaffId - Staff que registra (FK TAB_EMPLOYEES)
 * @param {boolean} data.problemsDrawingBlood - Problemas extrayendo sangre
 * @param {string} [data.comments] - Comentarios (max 500 caracteres)
 * @param {boolean} [data.patientReferredOut] - Paciente referido externamente
 * @param {boolean} [data.labOrderGiven] - Se entregó orden de laboratorio
 *
 * Ejemplo de llamada:
 * POST /api/LabPhlebotomy
 * Body: {
 *   "specialistReferralId": 1,
 *   "cipId": "CIP000001",
 *   "dateOfService": "2026-03-04",
 *   "appointmentTypeId": 18,
 *   "pcpId": 1,
 *   "locationId": 1,
 *   "employeeStaffId": 5,
 *   "problemsDrawingBlood": false,
 *   "comments": "Blood draw completed without issues.",
 *   "patientReferredOut": false,
 *   "labOrderGiven": true
 * }
 *
 * Respuesta exitosa (200):
 * { "result": 0, "message": "Lab phlebotomy created successfully.", "labPhlebotomyId": 1 }
 *
 * Posibles errores (400):
 * { "result": 1, "message": "CIP_ID is required." }
 * { "result": 2, "message": "Referral not found or inactive. Only patients with an active Specialist Referral can have Lab Phlebotomy." }
 * { "result": 3, "message": "This referral already has a Lab Phlebotomy record. Relationship is 1:1." }
 * { "result": 4, "message": "Date of service cannot be a future date." }
 */
export const createLabPhlebotomy = async (data) => {
  try {
    const response = await axios.post(API_URL, data, authHeader());
    return response.data;
  } catch (error) {
    logEndpointError("createLabPhlebotomy", "POST", API_URL, error);
    throw error;
  }
};

/**
 * Actualiza un registro de Lab Phlebotomy. Los campos specialistReferralId y cipId
 * son inmutables. No permite fechas futuras.
 *
 * @param {Object} data
 * @param {number} data.labPhlebotomyId - ID del registro a actualizar
 * @param {string} data.dateOfService - Fecha de servicio (YYYY-MM-DD, no futura)
 * @param {number} data.appointmentTypeId - Tipo de cita
 * @param {number} data.pcpId - PCP asignado
 * @param {number} data.locationId - Centro médico
 * @param {number} data.employeeStaffId - Staff
 * @param {boolean} data.problemsDrawingBlood - Problemas extrayendo sangre
 * @param {string} [data.comments] - Comentarios
 * @param {boolean} [data.patientReferredOut] - Paciente referido externamente
 * @param {boolean} [data.labOrderGiven] - Se entregó orden de laboratorio
 *
 * Ejemplo de llamada:
 * PUT /api/LabPhlebotomy
 * Body: { "labPhlebotomyId": 1, "dateOfService": "2026-03-04", "problemsDrawingBlood": true, ... }
 *
 * Respuesta exitosa (200):
 * { "result": 0, "message": "Lab phlebotomy updated successfully." }
 *
 * Posibles errores (400):
 * { "result": 1, "message": "Lab phlebotomy record not found or inactive." }
 * { "result": 2, "message": "Date of service cannot be a future date." }
 */
export const updateLabPhlebotomy = async (data) => {
  try {
    const response = await axios.put(API_URL, data, authHeader());
    return response.data;
  } catch (error) {
    logEndpointError("updateLabPhlebotomy", "PUT", API_URL, error);
    throw error;
  }
};

/**
 * Soft delete de un registro de Lab Phlebotomy (NUM_STATUS de 1 a 0).
 * NO afecta el Specialist Referral asociado.
 *
 * @param {number} id - ID del registro a eliminar
 *
 * Ejemplo de llamada:
 * DELETE /api/LabPhlebotomy/1
 *
 * Respuesta exitosa (200):
 * { "result": 0, "message": "Lab phlebotomy deleted successfully." }
 *
 * Posibles errores (400):
 * { "result": 1, "message": "Lab phlebotomy record not found or already inactive." }
 */
export const deleteLabPhlebotomy = async (id) => {
  const url = `${API_URL}/${id}`;
  try {
    const response = await axios.delete(url, authHeader());
    return response.data;
  } catch (error) {
    logEndpointError("deleteLabPhlebotomy", "DELETE", url, error);
    throw error;
  }
};

/**
 * Retorna la lista paginada de registros de Lab Phlebotomy con filtros opcionales.
 *
 * @param {Object} [filters]
 * @param {string} [filters.cipId] - Filtrar por paciente
 * @param {number} [filters.appointmentTypeId] - Filtrar por tipo de cita
 * @param {number} [filters.locationId] - Filtrar por centro médico
 * @param {number} [filters.pcpId] - Filtrar por PCP
 * @param {boolean} [filters.problemsDrawingBlood] - Filtrar por problemas de extracción
 * @param {string} [filters.dateFrom] - Fecha de servicio desde (YYYY-MM-DD)
 * @param {string} [filters.dateTo] - Fecha de servicio hasta (YYYY-MM-DD)
 * @param {number} [filters.page=1] - Página actual
 * @param {number} [filters.pageSize=20] - Registros por página
 *
 * Ejemplo de llamada:
 * GET /api/LabPhlebotomy/list?page=1&pageSize=20
 * GET /api/LabPhlebotomy/list?locationId=1&problemsDrawingBlood=true&dateFrom=2026-03-01&dateTo=2026-03-31
 *
 * Respuesta exitosa (200):
 * {
 *   "total": 25,
 *   "page": 1,
 *   "pageSize": 20,
 *   "totalPages": 2,
 *   "data": [
 *     {
 *       "labPhlebotomyId": 5,
 *       "specialistReferralId": 5,
 *       "cipId": "CIP000001",
 *       "dateOfService": "2026-03-04T00:00:00",
 *       "createDate": "2026-03-04T10:30:00",
 *       "patientName": "John Doe",
 *       "patientDob": "1985-03-15T00:00:00",
 *       "appointmentTypeId": 18,
 *       "appointmentTypeName": "Walk-In",
 *       "pcpId": 1,
 *       "pcpName": "Dr. Maria Martinez",
 *       "locationId": 1,
 *       "medicalCenter": "Fort Myers",
 *       "employeeStaffId": 5,
 *       "staffName": "Ana Lopez",
 *       "problemsDrawingBlood": false,
 *       "patientReferredOut": false,
 *       "labOrderGiven": true,
 *       "comments": "Blood draw completed without issues.",
 *       "specialistTypeName": "Cardiology",
 *       "specialistName": "Dr. John Smith"
 *     }
 *   ]
 * }
 */
export const getLabPhlebotomyList = async (filters = {}) => {
  const params = new URLSearchParams();
  if (filters.cipId) params.append("cipId", filters.cipId);
  if (filters.appointmentTypeId != null) params.append("appointmentTypeId", filters.appointmentTypeId);
  if (filters.locationId != null) params.append("locationId", filters.locationId);
  if (filters.pcpId != null) params.append("pcpId", filters.pcpId);
  if (filters.problemsDrawingBlood != null) params.append("problemsDrawingBlood", filters.problemsDrawingBlood);
  if (filters.dateFrom) params.append("dateFrom", filters.dateFrom);
  if (filters.dateTo) params.append("dateTo", filters.dateTo);
  if (filters.page != null) params.append("page", filters.page);
  if (filters.pageSize != null) params.append("pageSize", filters.pageSize);

  const url = `${API_URL}/list?${params.toString()}`;
  try {
    const response = await axios.get(url, authHeader());
    return response.data;
  } catch (error) {
    logEndpointError("getLabPhlebotomyList", "GET", url, error);
    throw error;
  }
};

/**
 * Retorna el detalle completo de un registro de Lab Phlebotomy por su ID.
 * Incluye datos del referral, paciente, especialista y auditoría.
 *
 * @param {number} id - ID del Lab Phlebotomy
 *
 * Ejemplo de llamada:
 * GET /api/LabPhlebotomy/1
 *
 * Respuesta exitosa (200):
 * {
 *   "labPhlebotomyId": 1,
 *   "specialistReferralId": 1,
 *   "cipId": "CIP000001",
 *   "dateOfService": "2026-03-04T00:00:00",
 *   "createDate": "2026-03-04T10:30:00",
 *   "updateDate": "2026-03-04T14:00:00",
 *   "status": 1,
 *   "patientName": "John Doe",
 *   "patientFirstName": "John",
 *   "patientLastName": "Doe",
 *   "patientDob": "1985-03-15T00:00:00",
 *   "patientAge": 41,
 *   "patientGender": "M",
 *   "patientPhone": "239-555-0100",
 *   "appointmentTypeId": 18,
 *   "appointmentTypeName": "Walk-In",
 *   "pcpId": 1,
 *   "pcpName": "Dr. Maria Martinez",
 *   "locationId": 1,
 *   "medicalCenter": "Fort Myers",
 *   "employeeStaffId": 5,
 *   "staffName": "Ana Lopez",
 *   "problemsDrawingBlood": true,
 *   "comments": "Patient had difficulty with venipuncture.",
 *   "patientReferredOut": false,
 *   "labOrderGiven": true,
 *   "specialistName": "Dr. John Smith",
 *   "specialistNpi": "1234567890",
 *   "specialistPhone": "239-555-0200",
 *   "specialistTypeId": 1,
 *   "specialistTypeName": "Cardiology",
 *   "urgencyLevelId": 3,
 *   "urgencyName": "Low Risk",
 *   "referralStatusId": 2,
 *   "referralStatusName": "Faxed to specialist",
 *   "icd10Codes": "E11.65,I10",
 *   "reasonForReferral": "Patient needs cardiology evaluation.",
 *   "employeeCreate": 5,
 *   "createdBy": "Ana Lopez",
 *   "employeeUpdate": 5,
 *   "updatedBy": "Ana Lopez"
 * }
 *
 * Error (404):
 * { "message": "Lab phlebotomy record not found." }
 */
export const getLabPhlebotomyById = async (id) => {
  const url = `${API_URL}/${id}`;
  try {
    const response = await axios.get(url, authHeader());
    return response.data;
  } catch (error) {
    logEndpointError("getLabPhlebotomyById", "GET", url, error);
    throw error;
  }
};

/**
 * Autocomplete de pacientes con referrals activos elegibles para Lab Phlebotomy
 * (referrals que NO tienen un Lab Phlebotomy activo). Retorna TOP 20.
 * Un mismo paciente puede aparecer múltiples veces (una fila por referral elegible).
 *
 * @param {string} [searchTerm] - CIP ID o nombre del paciente
 * @param {number} [locationId] - Filtro opcional por centro médico
 * @returns {Promise<Array>} Array de pacientes con referrals elegibles
 *
 * Ejemplo de llamada:
 * GET /api/LabPhlebotomy/patients-with-active-referrals?searchTerm=Garcia&locationId=5
 * GET /api/LabPhlebotomy/patients-with-active-referrals
 *
 * Respuesta exitosa (200):
 * [
 *   {
 *     "specialistReferralId": 150,
 *     "cipId": "CIP001234",
 *     "referralDate": "2026-02-15T00:00:00",
 *     "patientName": "Maria Garcia Lopez",
 *     "patientFirstName": "Maria",
 *     "patientLastName": "Garcia Lopez",
 *     "patientDob": "1985-06-20T00:00:00",
 *     "patientAge": 40,
 *     "patientGender": "F",
 *     "patientPhone": "305-555-1234",
 *     "specialistName": "Dr. Juan Perez",
 *     "specialistTypeId": 3,
 *     "specialistTypeName": "Cardiologist",
 *     "pcpId": 45,
 *     "pcpName": "Ana Rodriguez",
 *     "locationId": 5,
 *     "medicalCenter": "Miami Health Center"
 *   }
 * ]
 *
 * Sin resultados: [] (200 OK)
 */
export const getPatientsWithActiveReferrals = async (searchTerm, locationId) => {
  const params = new URLSearchParams();
  if (searchTerm) params.append("searchTerm", searchTerm);
  if (locationId != null) params.append("locationId", locationId);
  const query = params.toString();
  const url = `${API_URL}/patients-with-active-referrals${query ? `?${query}` : ""}`;
  try {
    const response = await axios.get(url, authHeader());
    return response.data;
  } catch (error) {
    logEndpointError("getPatientsWithActiveReferrals", "GET", url, error);
    throw error;
  }
};
